/**
 * Pure date/time conversion helpers shared between Create + Edit event flows.
 *
 * Lifted out of CreateEventModal so both `buildEventPayload` and
 * `mapEventToFormState` can use the same conversion semantics. Returns plain
 * JS objects (epoch seconds, JS Date) so consumers don't depend on
 * `@internationalized/date` types here.
 *
 * @module dateTime
 */
import { CalendarDateTime, CalendarDate, fromDate, getLocalTimeZone } from '@internationalized/date';

/**
 * Convert a `CalendarDateTime` (or compatible plain `{year,month,day,...}`
 * object) to Unix seconds. Returns `null` for falsy / invalid input.
 */
export function cdtToEpoch(cdt, targetTz) {
	if (!cdt) return null;
	const tz = targetTz || getLocalTimeZone();
	const real = new CalendarDateTime(
		cdt.year, cdt.month, cdt.day,
		cdt.hour ?? 0, cdt.minute ?? 0, cdt.second ?? 0
	);
	return Math.floor(real.toDate(tz).getTime() / 1000);
}

/**
 * Convert a `CalendarDateTime` / `CalendarDate` (or compatible plain object)
 * to a JS `Date`. Returns `null` for falsy / invalid input.
 */
export function cdtToJSDate(cdt, targetTz) {
	if (!cdt) return null;
	const tz = targetTz || getLocalTimeZone();
	const real = new CalendarDateTime(
		cdt.year, cdt.month, cdt.day,
		cdt.hour ?? 0, cdt.minute ?? 0, cdt.second ?? 0
	);
	return real.toDate(tz);
}

/** Format a `CalendarDate`-shaped value as `YYYY-MM-DD`. Empty string when null. */
export function formatYMD(cd) {
	if (!cd || cd.year == null) return '';
	const m = String(cd.month).padStart(2, '0');
	const d = String(cd.day).padStart(2, '0');
	return `${cd.year}-${m}-${d}`;
}

/**
 * Convert Unix-seconds epoch to a `CalendarDateTime` value the AppDatePicker
 * can consume. Used by `mapEventToFormState` when hydrating the edit form
 * from a `getEvent` response.
 */
export function epochToCalendarDateTime(epochSec, targetTz) {
	if (epochSec == null || epochSec === '') return null;
	const tz = targetTz || getLocalTimeZone();
	const seconds = Number(epochSec);
	if (!Number.isFinite(seconds)) return null;
	return fromDate(new Date(seconds * 1000), tz);
}

/** Parse a `YYYY-MM-DD` string into a `CalendarDate`, or return `null`. */
export function parseYMDToCalendarDate(s) {
	if (!s || typeof s !== 'string') return null;
	const m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
	if (!m) return null;
	const y = Number(m[1]);
	const mo = Number(m[2]);
	const d = Number(m[3]);
	if (!y || !mo || !d) return null;
	try { return new CalendarDate(y, mo, d); } catch { return null; }
}

/**
 * `CalendarDateTime` is exported so callers needing the constructor type
 * (e.g. instanceof checks) can import it from a single place. Internal use.
 */
export { CalendarDateTime };
